import { computed, makeObservable, observable } from 'mobx'

import {
  IPermitFieldsInput,
  ISitePermitInspection,
  ISitePermitInspectionInput,
  PermitFieldType,
} from '~/client/graph'
import { getWorkflowFieldKey } from '~/client/src/shared/utils/WorkflowFieldsHelper'

import getPermitFieldsDto from '../utils/getPermitFieldsDto'
import { copyObjectDeep } from '../utils/util'
import IPermitFieldDto from './IPermitFieldDto'
import PermitField from './PermitField'

export default class PermitInspection {
  public static fromDto(dto: ISitePermitInspection) {
    return new PermitInspection(
      dto.id,
      dto.permitId,
      dto.projectId,
      dto.userId,
      dto.createdAt,
      dto.fields,
      dto.inspectionDate,
    )
  }

  @observable public permitId: string = null
  @observable public projectId: string = null
  @observable public userId: string = null
  @observable public fields: PermitField[] = []
  @observable public createdAt?: number = null
  @observable public inspectionDate?: number = null

  public constructor(
    public id: string,
    permitId: string,
    projectId: string,
    userId: string,
    createdAt?: number,
    fields?: IPermitFieldDto[],
    inspectionDate?: number,
  ) {
    this.permitId = permitId
    this.projectId = projectId
    this.userId = userId

    this.createdAt = createdAt
    this.inspectionDate = inspectionDate

    this.fields = fields?.map(field => PermitField.fromDto(field)) || []
    makeObservable(this)
  }

  @computed
  private get fieldsByIdMap(): Record<string, PermitField> {
    return this.fields.reduce(
      (map, field) => {
        const key = getWorkflowFieldKey(
          field.fieldId,
          field.parentInstanceId,
          field.parentValueIndex,
        )
        map[key] = field
        return map
      },
      {} as Record<string, PermitField>,
    )
  }

  public getAllFieldsByType = (type: PermitFieldType): PermitField[] => {
    const allFields = [
      ...this.fields,
      ...this.fields
        .filter(f => f.type === PermitFieldType.Table)
        .flatMap(f => f.values.flat()),
    ]
    return allFields.filter(f => type === f.type)
  }

  public copy(): PermitInspection {
    const newObj = new PermitInspection(
      this.id,
      this.permitId,
      this.projectId,
      this.userId,
      this.createdAt,
      null,
      this.inspectionDate,
    )
    newObj.fields = this.fields?.map(f => copyObjectDeep(f)) || []

    return newObj
  }

  public getDto(): ISitePermitInspectionInput {
    return {
      id: this.id,
      permitId: this.permitId,
      projectId: this.projectId,
      userId: this.userId,
      inspectionDate: this.inspectionDate,
    }
  }

  public getFieldsDto(): IPermitFieldsInput {
    return getPermitFieldsDto(this.fields)
  }

  public getField = (
    fieldId: string,
    parentInstanceId: string,
    parentValueIndex: number,
  ): PermitField => {
    const key = getWorkflowFieldKey(fieldId, parentInstanceId, parentValueIndex)
    return this.fieldsByIdMap[key]
  }
}
